import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import Navbar from "@/components/Navbar";
import BankIcon from "@/assets/img/bank-icon.png";
import NoPayoutAccountIcon from "@/assets/img/bank.png";

import {
  UserCircle,
  KeyRound,
  BellRing,
  User,
  Eye,
  EyeOff,
  Shield,
  CreditCard,
} from "lucide-react";
import { getCurrentClientData, getUserSettings } from "@/services/authServices";
import UserSettiingsSkeleton from "@/components/UserSettiingsSkeleton";
import { EventsService } from "@/api/events";

interface User {
  name?: string;
  email: string;
  bio?: string;
  notifications?: {
    email: boolean;
    marketing: boolean;
  };
}

const Profile = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const loggedInUser = getCurrentClientData();
  const [user, setUser] = useState<User | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [activeTab, setActiveTab] = useState("profile");
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const { changePassword, userSettings, emailNotification } = EventsService;

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [payoutResponse, setPayoutResponse] = useState<any | null>(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const payoutStatus = loggedInUser?.payout;
  const [isReturningFromPayoutSetup, setIsReturningFromPayoutSetup] =
    useState(false);
  const {
    createPayoutAccount,
    updatePayoutAccount,
    getPayoutAccount,
  } = EventsService;

  useEffect(() => {
    if (loggedInUser.token) {
      getUserSettingsFn();
    } else {
      // Redirect to sign in if not authenticated
      navigate("/signin");
    }
  }, [navigate]);

  const getUserSettingsFn = async () => {
    setPageLoading(true);
    try {
      const { data } = await getUserSettings(loggedInUser.token);
      setPageLoading(false);
      setName(data?.body?.data?.full_name || "");
      setEmail(data?.body?.data?.email || "");
      setBio(data?.body?.data?.profile_description || "");
    } catch (error) {
      setPageLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const fullName = name.trim();
      const [givenName, ...rest] = fullName.split(" ");
      const familyName = rest.join(" ") || "";

      const payload = {
        given_name: givenName,
        family_name: familyName,
        email,
        profile_description: bio,
      };

      const res: any = await userSettings(loggedInUser.token, payload);

      if (res.statusCode === 200) {
        setIsLoading(false);

        localStorage.setItem("user", JSON.stringify(res.body.data));
        setUser(res.body.data);

        toast({
          title: "Profile updated",
          description: "Your profile has been successfully updated.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Update failed",
          description: "There was a problem updating your profile.",
        });
      }
      // Update user in localStorage
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "There was a problem updating your profile.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateNotifications = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        emailNotifications,
        marketingEmails,
      };

      const res: any = await emailNotification(loggedInUser.token, payload);

      if (res?.statusCode === 200) {
        toast({
          title: "Preferences updated",
          description: res.body.message,
        });
        setIsLoading(false);
      } else {
        toast({
          variant: "destructive",
          title: "Update failed",
          description:
            "There was a problem saving your notification preferences.",
        });
        setIsLoading(false);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description:
          "There was a problem saving your notification preferences.",
      });
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "New password and confirmation do not match.",
      });
      setIsLoading(false);
      return;
    }

    try {
      const res: any = await changePassword(loggedInUser.token, {
        current_password: currentPassword,
        new_password: newPassword,
      });

      if (res.statusCode === 200) {
        toast({
          title: "Password updated",
          description: "Your password has been successfully updated.",
        });
        setIsLoading(false);
      } else {
        toast({
          variant: "destructive",
          title: "Update failed",
          description: "There was a problem updating your password.",
        });
        setIsLoading(false);
      }

      // In a real app, you'd update the password via API
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "There was a problem updating your password.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePayoutAccount = async () => {
    if (!loggedInUser?.token) return;

    setPayoutLoading(true);
    try {
      const res: any = await createPayoutAccount(loggedInUser.token);
      const payload = res;
      const url = payload?.onboardingUrl;

      toast({
        title: "Payout account",
        description: payload?.message || "Request processed.",
      });

      if (url) {
        window.location.href = url; // replaces current page
      }
      const response: any = await getPayoutAccount(loggedInUser.token);
      setPayoutResponse(response?.payout);
      setIsReturningFromPayoutSetup(true);
    } catch (error) {
      toast({
        title: "Payout error",
        description: "Unable to create payout account.",
        variant: "destructive",
      });
      setIsReturningFromPayoutSetup(false);

      setPayoutLoading(false);
    } finally {
      setIsReturningFromPayoutSetup(false);

      setPayoutLoading(false);
    }
  };

  // const handleUpdatePayoutAccount = async () => {
  //   if (!loggedInUser?.token) return;
  //   setPayoutLoading(true);
  //   try {
  //     const res: any = await updatePayoutAccount(loggedInUser.token, {
  //       business_profile: {
  //         name: payoutStatus?.name
  //       }
  //     });

  //     toast({
  //       title: "Payout account",
  //       description: "Account name updated",
  //     });
  //   } catch (error) {
  //     toast({
  //       title: "Payout error",
  //       description: "Unable to update payout account.",
  //       variant: "destructive",
  //     });
  //   } finally {
  //     setPayoutLoading(false);
  //   }
  // };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Account Settings
            </h1>
            <p className="mt-2 text-gray-600">
              Manage your profile information, security settings, and
              preferences
            </p>
          </div>
          <Tabs
            defaultValue="profile"
            value={activeTab}
            onValueChange={setActiveTab}
            className="space-y-6"
          >
            <TabsList className="grid grid-cols-2 gap-2 lg:grid-cols-4 w-full bg-gray-100 p-1 rounded-lg">
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Profile</span>
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Security</span>
              </TabsTrigger>
              <TabsTrigger
                value="notifications"
                className="flex items-center gap-2"
              >
                <BellRing className="h-4 w-4" />
                <span className="hidden sm:inline">Notifications</span>
              </TabsTrigger>
              <TabsTrigger value="payout account" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                <span className="hidden sm:inline">Payout Account</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-4">
              {pageLoading ? (
                <>
                  <div className="text-sm font-bold text-gray-900 py-5">
                    Loading...
                  </div>
                  <UserSettiingsSkeleton />
                </>
              ) : (
                <Card>
                  <CardHeader>
                    <div className="flex items-center space-x-2">
                      <UserCircle className="h-5 w-5 text-gray-500" />
                      <CardTitle>Personal Information</CardTitle>
                    </div>
                    <CardDescription>
                      Update your personal details and public profile
                    </CardDescription>
                  </CardHeader>
                  <form onSubmit={handleUpdateProfile}>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          placeholder="John Doe"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                        <p className="text-sm text-muted-foreground">
                          This email will be used for account notifications and
                          recovery
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bio">Bio</Label>
                        <Textarea
                          id="bio"
                          placeholder="Tell us a little about yourself"
                          value={bio}
                          onChange={(e) => setBio(e.target.value)}
                          className="min-h-[100px]"
                        />
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-end space-x-4 border-t pt-6">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate("/dashboard")}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" disabled={isLoading}>
                        {isLoading ? "Saving..." : "Save Changes"}
                      </Button>
                    </CardFooter>
                  </form>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="security" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <KeyRound className="h-5 w-5 text-gray-500" />
                    <CardTitle>Password</CardTitle>
                  </div>
                  <CardDescription>
                    Update your password to maintain account security
                  </CardDescription>
                </CardHeader>
                <form onSubmit={handleUpdatePassword}>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="currentPassword">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="currentPassword"
                          type={showCurrentPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowCurrentPassword((prev) => !prev)
                          }
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                          tabIndex={-1}
                        >
                          {showCurrentPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <div className="relative">
                        <Input
                          id="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((prev) => !prev)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                          tabIndex={-1}
                        >
                          {showNewPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">
                        Confirm New Password
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? (
                            <EyeOff size={18} />
                          ) : (
                            <Eye size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-end space-x-4 border-t pt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setCurrentPassword("");
                        setNewPassword("");
                        setConfirmPassword("");
                      }}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? "Updating..." : "Update Password"}
                    </Button>
                  </CardFooter>
                </form>
              </Card>
            </TabsContent>

            <TabsContent value="notifications" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <BellRing className="h-5 w-5 text-gray-500" />
                    <CardTitle>Notification Preferences</CardTitle>
                  </div>
                  <CardDescription>
                    Manage how and when you receive notifications
                  </CardDescription>
                </CardHeader>
                <form onSubmit={handleUpdateNotifications}>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <h4 className="text-sm font-medium">
                          Email Notifications
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Receive notifications about account activity
                        </p>
                      </div>
                      <Switch
                        checked={emailNotifications}
                        onCheckedChange={setEmailNotifications}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <h4 className="text-sm font-medium">
                          Marketing Emails
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Receive updates about new features and promotions
                        </p>
                      </div>
                      <Switch
                        checked={marketingEmails}
                        onCheckedChange={setMarketingEmails}
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-end space-x-4 border-t pt-6">
                    <Button type="submit" disabled={isLoading}>
                      {isLoading ? "Saving..." : "Save Preferences"}
                    </Button>
                  </CardFooter>
                </form>
              </Card>
            </TabsContent>
            <TabsContent value="payout account" className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <CardTitle>Payout Account</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  {
                    payoutStatus?.connected ||
                      payoutResponse?.connected ?
                      <div className="rounded-lg border p-4 bg-[#F9FAFB] max-w-[593px] flex flex-col lg:flex-row lg:items-center justify-between">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                          <img
                            src={BankIcon}
                            className="w-[32px] h-[32px]"
                            alt="Bank"
                          />
                          <div>

                            <p className="text-sm text-gray-500 capitalize">
                              {payoutStatus?.name ||
                                payoutResponse?.name}{" "}
                            </p>

                            {(payoutStatus?.stripeAccountId ||
                              payoutResponse?.stripeAccountId) && (
                                <p className="text-xs text-gray-500 capitalize">
                                  {(payoutStatus?.bank_name || "")
                                    .charAt(0)
                                    .toUpperCase() +
                                    (payoutStatus?.bank_name || "")
                                      .slice(1)
                                      .toLowerCase() ||
                                    (payoutResponse?.bank_name || "")
                                      .charAt(0)
                                      .toUpperCase() +
                                    (payoutResponse?.bank_name || "")
                                      .slice(1)
                                      .toLowerCase()}
                                  •
                                  {payoutStatus?.bank_numbers
                                    ?.routing_number ||
                                    payoutResponse?.bank_numbers
                                      ?.routing_number}
                                  •
                                  {(
                                    payoutStatus?.currency ||
                                    payoutResponse?.currency ||
                                    ""
                                  ).toUpperCase()}
                                </p>
                              )}
                          </div>
                        </div>
                        {/* <div className="flex gap-2 mt-3 lg:mt-0">
                          {!isReturningFromPayoutSetup && (
                            <Button
                              type="button"
                              variant="outline"
                              color="#1D5AA5"
                              className="text-[#1D5AA5] font-semibold text-sm border-cecure-blue-200 bg-cecure-blue-50"
                              onClick={handleUpdatePayoutAccount}
                              disabled={payoutLoading}
                            >
                              {payoutLoading
                                ? "Processing..."
                                : "Change Account"}
                            </Button>
                          )}
                        </div> */}
                      </div>

                      :
                      <div className="rounded-lg  p-4 flex flex-col items-center justify-center">
                        <img
                          src={NoPayoutAccountIcon}
                          className="w-[120px] h-[120px]"
                          alt="Bank"
                        />
                        <p className=" font-bold mt-[48px] text-2xl mb-2">You have not added any payout account</p>
                        <p className="text-gray-500 mb-6">Add a payout account to receive your funds from your ticket sales</p>

                        <Button type="button" onClick={handleCreatePayoutAccount}>
                          Add Payout Account
                        </Button>
                      </div>
                  }

                </CardContent>

              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Profile;
